import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Box,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';

import { NotificationApi } from '@/api';
import { Page } from '@/components';
import {
  INotificationPreferences,
  ICaseNotificationPreferences,
  ITaskNotificationPreferences,
  ICalendarEventNotificationPreferences,
} from '@/models';
import { useUserStore, useToastStore } from '@/store';
import { ConfirmTypes } from '@/store/store.toast';

import {
  TabPanel,
  a11yProps,
  SettingItem,
  CfsDisplaySettings,
  CfsViewDisplaySettings,
  CreateCfsMethodSettings,
  NotificationSettings,
  SettingsTabContent,
  DefaultSettingDetail,
  UnitShiftDisplaySettings,
  AccountInformationForm,
} from './components';

// We use the same localStorage key as in PageOpenSettings.tsx
const CFS_DISPLAY_KEY = 'cfs-display-option';
const CFS_VIEW_DISPLAY_KEY = 'cfs-view-display-option';
const CREATE_CFS_METHOD_KEY = 'create-cfs-method';
const UNIT_SHIFT_DISPLAY_KEY = 'unit-shifts-display-option';

const DEFAULT_NOTIFICATION_PREFERENCES: INotificationPreferences = {
  _id: '',
  userId: '',
  caseNotifications: {
    newCaseAssigned: true,
    mentionedInCase: true,
    caseApproved: true,
    caseReassigned: true,
    caseRejected: true,
    caseReopened: true,
    caseUpdatedByOther: true,
  },
  taskNotifications: {
    taskAssigned: { inApp: true, email: false },
    taskMentioned: { inApp: true, email: false },
    taskMarkedComplete: { inApp: true, email: false },
    taskUpdated: { inApp: true, email: false },
  },
  calendarEventNotifications: {
    eventAssigned: { inApp: true, email: false },
    eventUpdated: { inApp: true, email: false },
    eventResponse: { inApp: true, email: false },
    eventReminder: { inApp: true, email: false },
    eventApproval: { inApp: true, email: false },
  },
  createdAt: '',
  updatedAt: '',
};

export default function SettingsPage() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, updateUserStore } = useUserStore();
  const { updateToast } = useToastStore();

  // Determine initial tab based on URL
  const getInitialTab = useCallback(() => {
    if (location.pathname.includes('/settings/notifications')) {
      return 2; // Notification Preferences tab
    }
    if (location.pathname.includes('/settings/profile')) {
      return 0; // Profile Settings tab
    }
    return 1; // Default to CFS Settings '/settings/cfs'
  }, [location.pathname]);

  const [tabValue, setTabValue] = useState(getInitialTab());
  const [selectedSetting, setSelectedSetting] = useState<string | null>(
    location.pathname.includes('/settings/notifications')
      ? 'case-notifications'
      : location.pathname.includes('/settings/profile')
      ? 'account-information'
      : null,
  );
  const [notificationPreferences, setNotificationPreferences] =
    useState<INotificationPreferences | null>(null);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(false);

  // CFS display option state - Get from localStorage first, then user settings, then default
  const defaultDisplayOption = useMemo(() => {
    const storedOption = localStorage.getItem(CFS_DISPLAY_KEY);
    return storedOption || user?.settings?.cfsDisplayOption || 'slide-view';
  }, [user?.settings?.cfsDisplayOption]);

  const [cfsDisplayOption, setCfsDisplayOption] =
    useState(defaultDisplayOption);

  // Unit shift display option state - Get from localStorage first, then user settings, then default
  const defaultUnitShiftDisplayOption = useMemo(() => {
    const storedOption = localStorage.getItem(UNIT_SHIFT_DISPLAY_KEY);
    return (
      storedOption || user?.settings?.unitShiftDisplayOption || 'slide-view'
    );
  }, [user?.settings?.unitShiftDisplayOption]);

  const [unitShiftDisplayOption, setUnitShiftDisplayOption] = useState(
    defaultUnitShiftDisplayOption,
  );

  // CFS view display option state - Get from localStorage first, then user settings, then default
  const defaultCfsViewDisplayOption = useMemo(() => {
    const storedOption = localStorage.getItem(CFS_VIEW_DISPLAY_KEY);
    return storedOption || user?.settings?.cfsViewDisplayOption || 'slide-view';
  }, [user?.settings?.cfsViewDisplayOption]);

  const [cfsViewDisplayOption, setCfsViewDisplayOption] = useState(
    defaultCfsViewDisplayOption,
  );

  // Create CFS method state - Get from localStorage first, then user settings, then default
  const defaultCreateCfsMethod = useMemo(() => {
    const storedOption = localStorage.getItem(CREATE_CFS_METHOD_KEY);
    return storedOption || user?.settings?.createCfsMethod || 'slide-view';
  }, [user?.settings?.createCfsMethod]);

  const [createCfsMethod, setCreateCfsMethod] = useState(
    defaultCreateCfsMethod,
  );

  // Fetch notification preferences when tab is selected
  const fetchNotificationPreferences = useCallback(async () => {
    setIsLoadingPreferences(true);
    try {
      const response = await NotificationApi.getNotificationPreferences();
      setNotificationPreferences(response.data);
    } catch (error) {
      // Set default preferences if API fails
      setNotificationPreferences(DEFAULT_NOTIFICATION_PREFERENCES);
    } finally {
      setIsLoadingPreferences(false);
    }
  }, []);

  useEffect(() => {
    if (tabValue === 2 && !notificationPreferences) {
      fetchNotificationPreferences();
    }
  }, [tabValue, notificationPreferences, fetchNotificationPreferences]);

  const handleNotificationPreferenceChange = useCallback(
    async (
      type: 'case' | 'task' | 'calendar-event',
      key:
        | keyof ICaseNotificationPreferences
        | keyof ITaskNotificationPreferences
        | keyof ICalendarEventNotificationPreferences,
      valueOrChannel: boolean | string,
      channelValue?: boolean,
    ) => {
      if (!notificationPreferences) return;

      // Get the current preferences for the specific type
      const currentTypePreferences =
        type === 'case'
          ? notificationPreferences.caseNotifications
          : type === 'calendar-event'
          ? notificationPreferences.calendarEventNotifications
          : notificationPreferences.taskNotifications;

      let updatedTypePreferences: any;

      const isChannelBasedType = type === 'calendar-event' || type === 'task';

      // Check if this is a channel-based update (for calendar events and tasks)
      if (
        isChannelBasedType &&
        typeof valueOrChannel === 'string' &&
        channelValue !== undefined
      ) {
        // Channel-based update: valueOrChannel is the channel name, channelValue is the boolean
        const channel = valueOrChannel as 'inApp' | 'email' | 'sms' | 'push';
        const currentEventPrefs = (currentTypePreferences as any)[key] || {};

        updatedTypePreferences = {
          ...currentTypePreferences,
          [key]: {
            ...currentEventPrefs,
            [channel]: channelValue,
          },
        };
      } else if (isChannelBasedType && typeof valueOrChannel === 'boolean') {
        // Fallback boolean update - apply to both channels
        updatedTypePreferences = {
          ...currentTypePreferences,
          [key]: { inApp: valueOrChannel, email: valueOrChannel },
        };
      } else {
        // Simple boolean update (for cases)
        updatedTypePreferences = {
          ...currentTypePreferences,
          [key]: valueOrChannel as boolean,
        };
      }

      // Create the update payload with the complete nested object
      const updatePayload = {
        [type === 'case'
          ? 'caseNotifications'
          : type === 'calendar-event'
          ? 'calendarEventNotifications'
          : 'taskNotifications']: updatedTypePreferences,
      };

      // Optimistically update the local state
      const updatedPreferences = {
        ...notificationPreferences,
        [type === 'case'
          ? 'caseNotifications'
          : type === 'calendar-event'
          ? 'calendarEventNotifications'
          : 'taskNotifications']: updatedTypePreferences,
      };

      setNotificationPreferences(updatedPreferences);

      try {
        await NotificationApi.updateNotificationPreferences(updatePayload);
        updateToast({
          open: true,
          message: 'Notification preferences updated successfully',
          type: ConfirmTypes.SUCCESS,
        });
      } catch (error) {
        // Revert the change on error
        setNotificationPreferences(notificationPreferences);
        updateToast({
          open: true,
          message: 'Failed to update notification preferences',
          type: ConfirmTypes.ERROR,
        });
      }
    },
    [notificationPreferences, updateToast],
  );

  const handleDisplayOptionChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setCfsDisplayOption(newValue);

      // Save immediately
      if (user?._id) {
        try {
          // Save to localStorage
          localStorage.setItem(CFS_DISPLAY_KEY, newValue);

          // Create updated settings object for API
          const updatedSettings = {
            ...(user?.settings || {}),
            cfsDisplayOption: newValue,
          };

          // Update local store
          updateUserStore({
            user: {
              ...user,
              settings: updatedSettings,
            },
          });

          updateToast({
            open: true,
            message: 'Settings updated successfully',
            type: ConfirmTypes.SUCCESS,
          });
        } catch (error) {
          updateToast({
            open: true,
            message: 'Failed to update settings',
            type: ConfirmTypes.ERROR,
          });
        }
      }
    },
    [user, updateUserStore, updateToast],
  );

  const handleUnitShiftDisplayOptionChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setUnitShiftDisplayOption(newValue);

      // Save immediately
      if (user?._id) {
        try {
          // Save to localStorage
          localStorage.setItem(UNIT_SHIFT_DISPLAY_KEY, newValue);

          // Create updated settings object for API
          const updatedSettings = {
            ...(user?.settings || {}),
            unitShiftDisplayOption: newValue,
          };

          // Update local store
          updateUserStore({
            user: {
              ...user,
              settings: updatedSettings,
            },
          });

          updateToast({
            open: true,
            message: 'Settings updated successfully',
            type: ConfirmTypes.SUCCESS,
          });
        } catch (error) {
          updateToast({
            open: true,
            message: 'Failed to update settings',
            type: ConfirmTypes.ERROR,
          });
        }
      }
    },
    [user, updateUserStore, updateToast],
  );

  const handleCfsViewDisplayOptionChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setCfsViewDisplayOption(newValue);

      // Save immediately
      if (user?._id) {
        try {
          // Save to localStorage
          localStorage.setItem(CFS_VIEW_DISPLAY_KEY, newValue);

          // Create updated settings object for API
          const updatedSettings = {
            ...(user?.settings || {}),
            cfsViewDisplayOption: newValue,
          };

          // Update local store
          updateUserStore({
            user: {
              ...user,
              settings: updatedSettings,
            },
          });

          updateToast({
            open: true,
            message: 'Settings updated successfully',
            type: ConfirmTypes.SUCCESS,
          });
        } catch (error) {
          updateToast({
            open: true,
            message: 'Failed to update settings',
            type: ConfirmTypes.ERROR,
          });
        }
      }
    },
    [user, updateUserStore, updateToast],
  );

  const handleCreateCfsMethodChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setCreateCfsMethod(newValue);

      // Save immediately
      if (user?._id) {
        try {
          // Save to localStorage
          localStorage.setItem(CREATE_CFS_METHOD_KEY, newValue);

          // Create updated settings object for API
          const updatedSettings = {
            ...(user?.settings || {}),
            createCfsMethod: newValue,
          };

          // Update local store
          updateUserStore({
            user: {
              ...user,
              settings: updatedSettings,
            },
          });

          updateToast({
            open: true,
            message: 'Settings updated successfully',
            type: ConfirmTypes.SUCCESS,
          });
        } catch (error) {
          updateToast({
            open: true,
            message: 'Failed to update settings',
            type: ConfirmTypes.ERROR,
          });
        }
      }
    },
    [user, updateUserStore, updateToast],
  );

  const profileSettings: SettingItem[] = useMemo(
    () => [
      {
        key: 'account-information',
        title: 'Account Information',
        description: 'Manage your account details',
        onClick: () => setSelectedSetting('account-information'),
      },
    ],
    [],
  );

  const cfsSettings: SettingItem[] = useMemo(
    () => [
      {
        key: 'create-cfs-method',
        title: 'Create CFS method',
        description: 'Configure how a new CFS should open',
        onClick: () => setSelectedSetting('create-cfs-method'),
      },
      {
        key: 'cfs-display',
        title: 'Active dispatch - View CFS',
        description: 'Configure how CFS from active dispatch should open',
        onClick: () => setSelectedSetting('cfs-display'),
      },
      {
        key: 'cfs-view-display',
        title: 'Call for service - View CFS',
        description:
          'Configure how CFS from the call for service screen should open',
        onClick: () => setSelectedSetting('cfs-view-display'),
      },
      {
        key: 'unit-shift-display',
        title: 'Unit shift',
        description: 'Configure how the Unit shift page should open',
        onClick: () => setSelectedSetting('unit-shift-display'),
      },
    ],
    [],
  );

  const notificationSettings: SettingItem[] = useMemo(
    () => [
      {
        key: 'case-notifications',
        title: 'Case Notifications',
        description:
          'Receive alerts about important case-related updates and activities',
        onClick: () => setSelectedSetting('case-notifications'),
      },
      {
        key: 'task-notifications',
        title: 'Task Notifications',
        description:
          'Get notified about key changes and updates to your assigned tasks',
        onClick: () => setSelectedSetting('task-notifications'),
      },
      {
        key: 'calendar-event-notifications',
        title: 'Calendar and Schedules Notifications',
        description:
          'Get notified about key changes and updates to your assigned events',
        onClick: () => setSelectedSetting('calendar-event-notifications'),
      },
    ],
    [],
  );

  const renderSettingDetail = useCallback(() => {
    if (!selectedSetting) {
      return <DefaultSettingDetail tabValue={tabValue} />;
    }

    switch (selectedSetting) {
      case 'account-information':
        return <AccountInformationForm />;
      case 'create-cfs-method':
        return (
          <CreateCfsMethodSettings
            createCfsMethod={createCfsMethod}
            onMethodChange={handleCreateCfsMethodChange}
          />
        );
      case 'cfs-display':
        return (
          <CfsDisplaySettings
            cfsDisplayOption={cfsDisplayOption}
            onDisplayOptionChange={handleDisplayOptionChange}
          />
        );
      case 'cfs-view-display':
        return (
          <CfsViewDisplaySettings
            cfsViewDisplayOption={cfsViewDisplayOption}
            onDisplayOptionChange={handleCfsViewDisplayOptionChange}
          />
        );
      case 'unit-shift-display':
        return (
          <UnitShiftDisplaySettings
            unitShiftDisplayOption={unitShiftDisplayOption}
            onDisplayOptionChange={handleUnitShiftDisplayOptionChange}
          />
        );
      case 'case-notifications':
        return (
          <NotificationSettings
            type="case"
            notificationPreferences={notificationPreferences}
            isLoadingPreferences={isLoadingPreferences}
            onNotificationPreferenceChange={handleNotificationPreferenceChange}
          />
        );
      case 'task-notifications':
        return (
          <NotificationSettings
            type="task"
            notificationPreferences={notificationPreferences}
            isLoadingPreferences={isLoadingPreferences}
            onNotificationPreferenceChange={handleNotificationPreferenceChange}
          />
        );
      case 'calendar-event-notifications':
        return (
          <NotificationSettings
            type="calendar-event"
            notificationPreferences={notificationPreferences}
            isLoadingPreferences={isLoadingPreferences}
            onNotificationPreferenceChange={handleNotificationPreferenceChange}
          />
        );
      default:
        return null;
    }
  }, [
    selectedSetting,
    tabValue,
    createCfsMethod,
    handleCreateCfsMethodChange,
    cfsDisplayOption,
    handleDisplayOptionChange,
    cfsViewDisplayOption,
    handleCfsViewDisplayOptionChange,
    unitShiftDisplayOption,
    handleUnitShiftDisplayOptionChange,
    notificationPreferences,
    isLoadingPreferences,
    handleNotificationPreferenceChange,
  ]);

  const tabs = useMemo(
    () => [
      {
        label: 'Profile Settings',
        value: 0,
        hidden: false,
        route: '/settings/profile',
      }, // not used for now
      {
        label: 'CFS Settings',
        value: 1,
        hidden: false,
        route: '/settings/cfs',
      },
      {
        label: 'Notification preferences',
        value: 2,
        hidden: false,
        route: '/settings/notifications',
      },
    ],
    [],
  );

  const visibleTabs = useMemo(() => {
    return tabs.filter((tab) => !tab.hidden);
  }, [tabs]);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newIndex: number) => {
      const selectedTab = visibleTabs[newIndex];
      // Map the visible tab index to the actual tab value
      const actualValue = selectedTab?.value ?? newIndex;
      setTabValue(actualValue);
      setSelectedSetting(null);

      // Update browser URL to reflect the selected tab
      if (selectedTab?.route) {
        navigate(selectedTab.route, { replace: true });
      }
    },
    [visibleTabs, navigate],
  );

  return (
    <Page title="Settings">
      <Stack
        spacing={2}
        sx={{
          flex: 1,
          p: 1,
          pt: 3,
          height: tabValue === 2 ? '100vh' : 'auto',
          overflow: tabValue === 2 ? 'hidden' : 'visible',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
            Settings
          </Typography>
        </Box>
        <Divider />
        <Paper
          sx={{
            minHeight: tabValue === 2 ? '600px' : 'auto',
            maxHeight: tabValue === 2 ? '85vh' : 'none',
            flex: tabValue === 2 ? 1 : 'none',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={visibleTabs.findIndex((tab) => tab.value === tabValue)}
              onChange={handleTabChange}
              aria-label="settings tabs"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 'medium',
                  fontSize: '0.95rem',
                  minWidth: 0,
                  px: 2,
                  py: 2,
                  color: theme.palette.text.secondary,
                },
                '& .Mui-selected': {
                  color: `${theme.palette.error.main} !important`,
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: theme.palette.error.main,
                  height: 2,
                  transform: 'scaleX(0.8)',
                  transformOrigin: 'center',
                },
              }}
            >
              {visibleTabs.map((tab) => (
                <Tab
                  key={tab.value}
                  label={tab.label}
                  {...a11yProps(tab.value)}
                />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            <TabPanel value={tabValue} index={0}>
              <SettingsTabContent
                title="Profile Settings"
                description="Manage your personal information and account preferences"
                settings={profileSettings}
                selectedSetting={selectedSetting}
              >
                {renderSettingDetail()}
              </SettingsTabContent>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <SettingsTabContent
                title="CFS Settings"
                description="Configure how CFS content is displayed and managed"
                settings={cfsSettings}
                selectedSetting={selectedSetting}
              >
                {renderSettingDetail()}
              </SettingsTabContent>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <SettingsTabContent
                title="Notification Preferences"
                description="Choose which events you want to be notified about and how you want to receive them"
                settings={notificationSettings}
                selectedSetting={selectedSetting}
              >
                {renderSettingDetail()}
              </SettingsTabContent>
            </TabPanel>
          </Box>
        </Paper>
      </Stack>
    </Page>
  );
}
